import type { WidgetType } from "@csbi/contracts"
import { SERIES_SLOTS } from "./palette.js"
import { MAX_GAUGE_PANELS } from "./options/gauge.js"

export type Downgrade = {
  from: WidgetType
  to: WidgetType
  reason: string
}

export type DowngradeInput = {
  widget: WidgetType
  categories: number
  series: number
  units: readonly string[]
}

const MAX_AXIS_CATEGORIES = 60
/** Past this adjacent slices cannot be told apart */
const MAX_PIE_SLICES = 6

export function resolveWidget(input: DowngradeInput): { widget: WidgetType; downgrade?: Downgrade } {
  const { widget, categories, series, units } = input

  const down = (to: WidgetType, reason: string) => ({
    widget: to,
    downgrade: { from: widget, to, reason },
  })

  if (units.length > 1 && widget !== "table")
    return down("table", "This tile mixes metrics of different units. Two scales on one chart invent a correlation that is not in "
      + "the data, so it is shown as a table instead.")

  /**
   * ⚠ The exemption is not optional. `associated_country` has only 43 jurisdictions locally, so
   * this rule **happens** not to fire today; the full dataset has 184. Without the exemption the
   * map works fine locally and then quietly turns into a table one day after the second or third
   * deployment is onboarded — with no error, and nobody would connect it to this line of code.
   */
  if (widget !== "map" && categories > MAX_AXIS_CATEGORIES)
    return down("table", `The grouping produced ${categories} categories. Past ${MAX_AXIS_CATEGORIES} the axis is unreadable, `
      + `so it is shown as a table instead.`)

  if (widget === "gauge" && !units.every((u) => u === "ratio"))
    return down(
      "stat-tile",
      "A gauge shows a value against a scale, and this metric has no declared maximum to scale it against. "
      + "The number itself is shown instead — it is the same information the centre of the gauge would carry.",
    )

  // Several customers on one gauge: whose needle is the shared one? Stacked needles cannot be
  // read. Many categories can become small multiples (one small gauge each); many customers
  // cannot — that is a comparison, and comparisons belong to length, not angle.
  if (widget === "gauge" && series > 1)
    return down(
      "hbar",
      `A gauge shows one value, and this tile has ${series} deployments to compare. Comparing angles is harder `
      + "than comparing lengths, so it is shown as a horizontal bar chart.",
    )

  if (widget === "gauge" && categories > MAX_GAUGE_PANELS)
    return down(
      "hbar",
      `A gauge shows one value, and this tile has ${categories} to compare. Comparing angles is harder than `
      + "comparing lengths, so it is shown as a horizontal bar chart.",
    )

  if (widget === "map" && series > 3)
    return down("hbar", `A map can carry at most 3 series and this tile has ${series}, so it is shown as a horizontal bar chart.`)

  if (widget === "pie") {
    if (categories > MAX_PIE_SLICES)
      return down("hbar", `This pie has ${categories} slices. Past ${MAX_PIE_SLICES} adjacent slices cannot be told apart, so it is `
        + `shown as a single-hue horizontal bar chart.`)
    if (categories === 2)
      return down("stat-tile", "A two-slice pie says the same thing as one number, so it is shown as a stat tile with a share bar.")
  }

  if (widget === "bar" && categories <= 1 && series <= 1)
    return down("stat-tile", "A bar chart with a single bar says the same thing as one number, so it is shown as a stat tile.")

  if (series > SERIES_SLOTS && widget !== "table" && widget !== "stat-tile")
    return down(
      "table",
      `${series} series exceeds the ${SERIES_SLOTS} categorical colour slots, so colour can no longer carry `
      + `identity. Shown as a table — small multiples is the other option.`,
    )

  return { widget }
}
